import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../utils/api';
import { getWeekNumber, formatDate, getDateByWeekAndDay, setServerWeekNumber } from '../utils/dateUtils';

const useScheduleLogic = () => {
  const [course, setCourse] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [cachedScheduleData, setCachedScheduleData] = useState(null);
  const [pairsTime, setPairsTime] = useState(null);
  const [cachedPairsTime, setCachedPairsTime] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('day');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showCachedData, setShowCachedData] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchWeekNumbers = useCallback(async () => {
    try {
      const result = await apiService.getWeekNumbers();
      const data = result?.data || result;
      const weekNum = data?.current_week_number ?? data?.week_number;
      if (weekNum != null) {
        setServerWeekNumber(weekNum);
        const week = weekNum;
        setCurrentWeek(week);
        const weeks = [];
        for (let i = Math.max(1, week - 4); i <= week + 4; i++) weeks.push(i);
        setAvailableWeeks(weeks);
      } else {
        const week = getWeekNumber(new Date());
        setCurrentWeek(week);
        setAvailableWeeks([week - 1, week, week + 1]);
      }
    } catch {
      const week = getWeekNumber(new Date());
      setCurrentWeek(week);
      setAvailableWeeks([week - 1, week, week + 1]);
    }
  }, []);

  const loadInitialSettings = useCallback(async () => {
    try {
      const savedCourse = localStorage.getItem('selected_course');
      const savedGroup = localStorage.getItem('selected_group');
      if (savedCourse) {
        setCourse(parseInt(savedCourse, 10));
      }
      if (savedGroup) {
        try {
          const parsed = JSON.parse(savedGroup);
          setSelectedGroup(parsed);
        } catch {
          setSelectedGroup(savedGroup);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Initialize
  useEffect(() => {
    loadInitialSettings();
    fetchWeekNumbers();
  }, [loadInitialSettings, fetchWeekNumbers]);

  // Fetch groups when course changes
  useEffect(() => {
    if (course == null) return;
    fetchGroupsForCourse(course);
  }, [course]);

  // Fetch pairs time on mount
  useEffect(() => {
    fetchPairsTime();
  }, []);

  // Fetch schedule when group/view/date/week changes
  useEffect(() => {
    if (selectedGroup) {
      fetchScheduleData(selectedGroup);
    }
  }, [selectedGroup, viewMode, currentDate, currentWeek]);

  const fetchGroupsForCourse = async (courseId) => {
    setLoadingGroups(true);
    setError(null);
    try {
      const result = await apiService.getGroups(courseId);
      const data = result?.data || result;
      const groupsArr = Array.isArray(data) ? data : (data?.groups || []);
      if (groupsArr.length > 0) {
        setGroups(groupsArr);
      }
    } catch (e) {
      setError('load-error');
      const cached = localStorage.getItem(`groups_${courseId}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const arr = parsed?.data || parsed;
          if (Array.isArray(arr)) setGroups(arr);
        } catch { /* ignore */ }
      }
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchPairsTime = async () => {
    try {
      const result = await apiService.getPairsTime();
      const data = result?.data || result;
      const arr = Array.isArray(data) ? data : (data?.pairs_time || null);
      if (arr) setPairsTime(arr);
    } catch {
      const cached = localStorage.getItem('cache_pairs_time');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.data) setCachedPairsTime(parsed.data);
        } catch { /* ignore */ }
      }
    }
  };

  const fetchScheduleData = async (group) => {
    if (!group) return;
    setLoadingSchedule(true);
    setError(null);
    setShowCachedData(false);

    const groupId = typeof group === 'string' ? group : (group.id || group.name || group);

    try {
      let result;
      if (viewMode === 'day') {
        const dateStr = formatDate(currentDate);
        result = await apiService.getSchedule(groupId, dateStr);
      } else {
        if (currentWeek) {
          result = await apiService.getSchedule(groupId, null, currentWeek);
        }
      }
      const data = result?.data || result;
      if (data) {
        setScheduleData(data);
        setCachedScheduleData(data);
      }
    } catch (e) {
      setError(!navigator.onLine ? 'no-internet' : 'load-error');
      if (cachedScheduleData) {
        setScheduleData(cachedScheduleData);
        setShowCachedData(true);
      }
    } finally {
      setLoadingSchedule(false);
      setRefreshing(false);
    }
  };

  const navigateDate = useCallback((direction) => {
    if (viewMode === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + direction);
      setCurrentDate(newDate);
    } else {
      setCurrentWeek(prev => prev + direction);
    }
  }, [viewMode, currentDate]);

  const handleRetry = useCallback(() => {
    if (selectedGroup) fetchScheduleData(selectedGroup);
  }, [selectedGroup, viewMode, currentDate, currentWeek]);

  const handleViewCache = useCallback(() => {
    if (cachedScheduleData) {
      setScheduleData(cachedScheduleData);
      setShowCachedData(true);
      setError(null);
    }
  }, [cachedScheduleData]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    if (selectedGroup) fetchScheduleData(selectedGroup);
  }, [selectedGroup, viewMode, currentDate, currentWeek]);

  const handleCourseChange = useCallback((newCourse) => {
    setCourse(newCourse);
    localStorage.setItem('selected_course', String(newCourse));
    setSelectedGroup(null);
    setGroups([]);
    setScheduleData(null);
  }, []);

  const handleGroupChange = useCallback((group) => {
    setSelectedGroup(group);
    const val = typeof group === 'string' ? group : JSON.stringify(group);
    localStorage.setItem('selected_group', val);
  }, []);

  return {
    course,
    setCourse: handleCourseChange,
    groups,
    selectedGroup,
    setSelectedGroup: handleGroupChange,
    scheduleData,
    pairsTime: pairsTime || cachedPairsTime,
    loadingGroups,
    loadingSchedule,
    refreshing,
    viewMode,
    setViewMode,
    currentDate,
    setCurrentDate,
    currentWeek,
    setCurrentWeek,
    availableWeeks,
    error,
    isOnline,
    showCachedData,
    cacheInfo,
    currentTime,
    navigateDate,
    handleRetry,
    handleViewCache,
    onRefresh,
  };
};

export default useScheduleLogic;
